<template>
  <div class="publicProfile">
    <img
      :src="profile.avatar"
      class="publicProfile__avatar"
    />

    <p class="publicProfile__name">{{ profile.firstName }} {{ profile.lastName }}</p>

    <div class="publicProfile__info">
      <span v-if="profile.position" class="publicProfile__position">
        <gl-svg-icon class="publicProfile__location__icon icon" icon="human" />
        {{ profile.position }}
      </span>
    </div>

    <profile-social-links :links="socialLinks" />

    <p class="publicProfile__description">
      {{
        $t(
          "1 600 000 people have already joined us, and our ultimate goal is to grow our community to 5 000 000 members by the end of 2023! That’s why we’re constantly improving our technologies and the partnership conditions for our users."
        )
      }}
    </p>

    <div class="publicProfile__metrics metrics">
      <div
        class="metrics__item"
        v-for="(metric, index) in metrics"
        :key="index"
      >
        <gl-svg-icon
          class="metrics__item__logo"
          :icon="metric.logo"
          width="48"
          height="48"
        />

        <div>
          <p class="metrics__item__value">{{ metric.value }}</p>

          <p class="metrics__item__title">{{ metric.title }}</p>
        </div>
      </div>
    </div>

    <a :href="toRegistration" class="metrics__item__btn" target="_blank">
      {{ $t("Sign up") }}
    </a>
  </div>
</template>

<script>
import GlSvgIcon from "@gl/GlSvgIcon";
import ProfileSocialLinks from "@/components/ProfileSocialLinks";
import { toRegistration } from "@/tools";

export default {
  components: { GlSvgIcon, ProfileSocialLinks },
  name: "PublicProfile",
  computed: {
    profile() {
      return this.$store.getters.getProfile
    },
    socialLinks() {
      return [
        {
          name: "phone",
          title: this.$t("Phone Number"),
          url: this.profile.mobile
            ? `tel:${this.profile.mobile}`
            : null,
        },
        {
          name: "whatsapp",
          title: "WhatsApp",
          // whatsapp://send?phone=[number] (without '+')
          // https://wa.me/[number] (without '+')
          url: this.profile.whatsapp
            ? `whatsapp://send?phone=${this.getNumberWithoutPlus(this.profile.whatsapp)}`
            : null,
        },
        {
          name: "telegram",
          title: "Telegram",
          // tg://resolve?domain=[username]
          url: this.profile.telegram
            ? `tg://resolve?domain=${this.profile.telegram}`
            : null,
        },
        {
          name: "instagram",
          title: "Instagram",
          // https://www.instagram.com/[username]
          url: this.profile.instagram
            ? `https://www.instagram.com/${this.profile.instagram}`
            : null,
        },
        {
          name: "skype",
          title: "Skype",
          // skype:[nickname]?chat
          url: this.profile.skype
            ? `skype:${this.profile.skype}?chat`
            : null,
        },
        {
          name: "viber",
          title: "Viber",
          //viber://chat?number=[number]
          url: this.profile.viber
            ? `viber://chat?number=${this.getNumberForViberDesktop(this.profile.viber)}`
            : null,
        },
        {
          name: "wechat",
          title: "WeChat",
          //weixin://dl/chat?[id]
          url: this.profile.wechat
            ? `weixin://dl/chat?${this.profile.wechat}`
            : null,
        },
      ];
    },
    metrics() {
      return [
        {
          logo: "group",
          value: "1 600 000+",
          title: this.$t("Users"),
        },
        {
          logo: "money",
          value: this.$t("up to 70%"),
          title: this.$t("Reward Plan"),
        },
        {
          logo: "profits",
          value: this.$t("up to 10 000 000 EUR"),
          title: this.$t("Big Bonuses"),
        },
        {
          logo: "earth-globe",
          value: "120+",
          title: this.$t("Countries"),
        },
        {
          logo: "relationship",
          value: "4+",
          title: this.$t("Offices"),
        },
      ];
    },
    toRegistration,
  },
  methods: {
    handleClickPhoneLink({ name, value }, event) {
      this.socialMediaName = name;
      this.phoneNumberForPopup = value;
      this.isShowPopup = true;
      event.preventDefault();
    },
    handleClickClosePopup() {
      this.isShowPopup = false;
    },
    getNumberWithoutPlus(number) {
      return number.slice(0, 1) === "+" ? number.substr(1) : number;
    },
    getNumberForViberDesktop(number) {
      const plus = "%2B";
      return number.slice(0, 1) === "+"
        ? number.replace("+", plus)
        : plus + number;
    },
  },
  data() {
    return {
      isShowPopup: false,
      socialMediaName: null,
      phoneNumberForPopup: null,
    };
  },
};
</script>

<style lang="scss" scoped>
.publicProfile {
  padding: 0 16px 64px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (min-width: $breakpoint-md) {
    padding: 0 24px 64px;
  }

  @media (min-width: $breakpoint-xl) {
    padding: 0 32px 90px;
  }

  &__location {
    @media (min-width: $breakpoint-md) {
      margin-right: 16px;
    }
  }

  &__avatar {
    width: 144px;
    border-radius: 50%;
    margin-top: -72px;

    @media (min-width: $breakpoint-md) {
      width: 172px;
      margin-top: -86px;
    }
  }

  &__name {
    @include text-h4;

    margin-top: 24px;

    @media (min-width: $breakpoint-md) {
      font-size: 18px;
      margin-top: 32px;
    }
  }

  &__info {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    font-family: $font-secondary;
    font-weight: bold;
    font-size: 14px;
    line-height: 24px;
    color: rgba(0, 0, 0, 0.38);
    text-align: center;

    @media (min-width: $breakpoint-md) {
      flex-direction: row;
    }
  }

  &__description {
    margin-top: 32px;
    font-size: 14px;
    line-height: 24px;
    text-align: center;
    padding: 0;

    @media (min-width: $breakpoint-md) {
      margin-top: 30px;
      font-size: 16px;
      padding: 0 54px;
    }

    @media (min-width: $breakpoint-xl) {
      padding: 0 72px;
    }
  }
}
.socialMedia {
  display: flex;
  width: 271px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 24px;

  @media (min-width: $breakpoint-md) {
    width: 100%;
    margin-top: 40px;
  }

  &__item {
    padding: 0 16px;
  }
}

.metrics {
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  width: 100%;

  @media (min-width: $breakpoint-md) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }

  @media (min-width: $breakpoint-xl) {
    margin-top: 32px;
  }

  &__item {
    border: 1px solid #e0e0e0;
    padding: 16px;
    width: 100%;
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: 8px;
    }

    @media (min-width: $breakpoint-md) {
      flex-direction: column;
      width: 208px;
      text-align: center;
      margin: 5px;
    }

    @media (min-width: $breakpoint-xl) {
      padding: 32px 0;
    }

    &__value {
      font-family: $font-secondary;
      font-size: 16px;
      font-weight: 700;
      line-height: 32px;

      @media (min-width: $breakpoint-xl) {
        font-size: 18px;
      }
    }

    &__title {
      font-family: $font-family;
      font-weight: 400;
      font-size: 14px;
      line-height: 24px;
      color: rgba(0, 0, 0, 0.87);
    }

    &__logo {
      width: 48px;
      height: 48px;
      margin-right: 16px;

      @media (min-width: $breakpoint-md) {
        width: 64px;
        height: 64px;
        margin-right: 0;
        margin-bottom: 16px;
      }
    }

    &__btn {
      padding: 20px 0px;
      text-align: center;
      font-size: 16px;
      color: #fff;
      font-weight: 700;
      line-height: 24px;
      width: 100%;
      background-color: $color-blue-4;
      margin-top: 48px;

      &:hover {
        background-color: #1f4776;
      }

      &:active {
        background-color: #18375b;
      }

      @media (min-width: $breakpoint-md) {
        max-width: 224px;
        margin-top: 32px;
      }
      @media (min-width: $breakpoint-xl) {
        max-width: 268px;
        margin-top: 48px;
      }
    }
  }
}

.popupContent {
  text-align: center;

  &__title {
    color: #00000061;
    font-size: 18px;
    line-height: 24px;
    font-family: $font-secondary;
    font-weight: 700;
  }

  &__text {
    font-size: 24px;
    line-height: 30px;
    font-family: $font-secondary;
    font-weight: 700;
    margin-top: 8;
    @media (min-width: $breakpoint-xl) {
      margin-top: 16px;
    }
  }

  &__btn {
    margin-top: 24px;

    @media (min-width: $breakpoint-xl) {
      margin-top: 32px;
    }
  }
}

.icon {
  width: 16px;
  height: 16px;

  @media (min-width: $breakpoint-md) {
    width: 24px;
    height: 24px;
  }
}
</style>
